export interface SerializedURLBuilder {
    _filters: any[];
    _baseParams: any[];
    _root: string;
    _searchString: string;
    _webcatList: string;
    _redirectKeyword: any;
}

export class URLBuilder {
    private _filters: any[] = [];

    constructor(
        private _root: string = ``,
        private _webcatList: string = ``,
        private _searchString?: string,
        private _baseParams: string[] = [],
        private _redirectKeyword?: any,
    ) {}

    /**
     * Adds a filter to the given object but does not build/return a URL (for adding multiple filters before building)
     * @param field
     * @param value
     */
    public addFilter(field: string, value: string): void {
        if (field === `purchasedBy_s_M`) {
            value = `on`;
        }
        this._filters.push({
            field: encodeURIComponent(field),
            value: encodeURIComponent(URLBuilder.cleanQuotes(value)),
        });
    }

    /**
     * TBD
     * @param filterValue
     * @private
     */
    public static cleanQuotes(filterValue: string) {
        let strippedValue: string;
        if (filterValue && typeof filterValue === `string` && filterValue.charAt(0) === `"`) {
            strippedValue = filterValue.substring(1, filterValue.length - 1);
        } else {
            strippedValue = filterValue;
        }
        return strippedValue;
    }

    /**
     * Builds and returns the current URL
     * @return string The current url
     */
    public currentURL(): string {
        return this._buildURL(this._root, this._webcatList, this._filters);
    }

    /**
     * Removes any filters for the given field and adds back a filter for the specific value
     * @param field
     * @param value
     * @return string The new url
     */
    public swapFilter(field: string, value: string): string {
        const newFilters = [];
        this._filters.forEach((existingFilter) => {
            if (existingFilter.field.toLowerCase() !== field.toLowerCase()) {
                newFilters.push(existingFilter);
            }
        });
        newFilters.push({
            field: field,
            value: value,
        });
        return this._buildURL(this._root, this._webcatList, newFilters);
    }

    /**
     * Adds a filter parameter to the current search/browse URL
     * @param field
     * @param value
     * @return string The new url
     */
    public withFilter(field: string, value: string): string {
        const newFilters = [];
        this._filters.forEach((filter) => {
            newFilters.push(filter);
        });
        newFilters.push({
            field: field,
            value: value,
        });
        return this._buildURL(this._root, this._webcatList, newFilters);
    }

    /**
     * Removes all filters from the current search/browse URL
     * @return string The new url
     */
    public withNoFilters(): string {
        return this._buildURL(this._root, this._webcatList, []);
    }

    /**
     * Removes the specified filter from the current search/browse URL
     * @param field
     * @param value
     * @return string The new url
     */
    public withoutFilter(field, value): string {
        // Override purchasedBy_s_M to always be 'on'
        if (field === `purchasedBy_s_M`) {
            value = `on`;
        }

        // Return buildURL() without the specified filter
        const newFilters = [];
        const newFilterValue = URLBuilder.cleanQuotes(value);
        this._filters.forEach((existingFilter) => {
            const existingFilterValue = decodeURIComponent(existingFilter.value);
            const filterFieldAlreadyExists = existingFilter.field.indexOf(field) >= 0;
            if (!(filterFieldAlreadyExists && newFilterValue === existingFilterValue)) {
                newFilters.push(existingFilter);
            }
        });
        return this._buildURL(this._root, this._webcatList, newFilters);
    }

    /**
     * Adds a parameter to the current URL
     * @param name
     * @param value
     * @return string The new url
     */
    public withParam(name: string, value: string): string {
        return URLBuilder._addParam(this._buildURL(this._root, this._webcatList, this._filters), `${name}=${value}`);
    }

    /**
     * Replaces or adds a new webcat to the current URL
     * @param newWebcat
     * @return string The new url
     */
    public withWebcat(newWebcat: string): string {
        return this._buildURL(this._root, newWebcat, this._filters);
    }

    /**
     * Add a parameter to the base URL
     * @param base
     * @param param
     * @private
     */
    private static _addParam(base: string, param: string): string {
        if (base.indexOf(`?`) >= 0) {
            return `${base}&${param}`;
        } else {
            return `${base.trim()}?${param}`;
        }
    }

    /**
     * Build a URL from a root, webcatlist and filters array
     * @param root
     * @param webcatlist
     * @param filters
     * @private
     */
    private _buildURL(root: string, webcatlist: string, filters: any[]): string {
        let newURL = `${root}${webcatlist}`;
        filters.forEach((filter, count) => {
            filter.value = URLBuilder.cleanQuotes(filter.value);
            if (count === 0) {
                newURL += `?`;
            } else {
                newURL += `&`;
            }
            newURL += `F${filter.field}=%22${filter.value}%22`;
        });
        this._baseParams.forEach((param) => {
            newURL = URLBuilder._addParam(newURL, param);
        });
        if (this._searchString) {
            return URLBuilder._addParam(newURL, `SearchString=${encodeURI(this._searchString)}`);
        } else if (this._redirectKeyword) {
            return URLBuilder._addParam(newURL, `redirectKeyword=${encodeURI(this._redirectKeyword)}`);
        } else {
            return newURL;
        }
    }

    /**
     * Create a URLBuilder from a serialized list of params
     * @param serialized
     */
    public static fromSerialized(serialized: SerializedURLBuilder) {
        const newBuilder = new URLBuilder(
            serialized._root,
            serialized._webcatList,
            serialized._searchString,
            serialized._baseParams,
            serialized._redirectKeyword,
        );
        if (serialized._filters?.length > 0) {
            newBuilder._filters = serialized._filters;
        }
        return newBuilder;
    }

    /**
     * Get a serialized version of the urlBuilder
     */
    public serialize(): SerializedURLBuilder {
        return {
            _filters: this._filters,
            _root: this._root,
            _webcatList: this._webcatList,
            _searchString: this._searchString,
            _baseParams: this._baseParams,
            _redirectKeyword: this._redirectKeyword,
        };
    }
}
